import React, { useEffect, useRef, useState } from "react";

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

const SecureDocumentViewer = ({
  url,
  title,
  index = 0,
  total = 1,
  onPrevious,
  onNext,
  onClose,
  loading = false,
  text
}) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [imageReady, setImageReady] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
    setDragging(false);
    setImageReady(false);
    dragRef.current = null;
  }, [url]);

  useEffect(() => {
    if (!url) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", closeOnEscape, true);
    return () => window.removeEventListener("keydown", closeOnEscape, true);
  }, [onClose, url]);

  if (!url) return null;

  const reset = () => {
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
  };

  const startPan = (event) => {
    // React/JSDOM can omit `button` for synthetic pointer events. Browsers send
    // 0 for the primary mouse button; only reject an explicitly non-primary one.
    if (loading || (event.button != null && event.button !== 0)) return;
    event.preventDefault();
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      panX: pan.x,
      panY: pan.y
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragging(true);
  };

  const movePan = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    setPan({
      x: drag.panX + event.clientX - drag.startX,
      y: drag.panY + event.clientY - drag.startY
    });
  };

  const stopPan = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    dragRef.current = null;
    setDragging(false);
  };

  const busy = loading || !imageReady;

  return (
    <div
      className="secure-document-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="secure-document-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className="secure-document-viewer">
        <header className="secure-document-header">
          <div>
            <p>{text.securePreview}</p>
            <h2 id="secure-document-title">{title}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label={text.closeViewer}>
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <div className="secure-document-controls" role="toolbar" aria-label={text.viewerControls}>
          <button
            type="button"
            aria-label={text.zoomOut}
            disabled={zoom <= MIN_ZOOM}
            onClick={() => setZoom((value) => Math.max(MIN_ZOOM, value - ZOOM_STEP))}
          >
            <i className="fa-solid fa-magnifying-glass-minus" aria-hidden="true" />
          </button>
          <span aria-live="polite">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            aria-label={text.zoomIn}
            disabled={zoom >= MAX_ZOOM}
            onClick={() => setZoom((value) => Math.min(MAX_ZOOM, value + ZOOM_STEP))}
          >
            <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label={text.rotateLeft}
            onClick={() => setRotation((value) => value - 90)}
          >
            <i className="fa-solid fa-rotate-left" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label={text.rotateRight}
            onClick={() => setRotation((value) => value + 90)}
          >
            <i className="fa-solid fa-rotate-right" aria-hidden="true" />
          </button>
          <button type="button" aria-label={text.resetView} onClick={reset}>
            <i className="fa-solid fa-arrows-rotate" aria-hidden="true" />
          </button>
        </div>
        <div className="secure-document-stage" aria-busy={busy}>
          {busy && (
            <div className="secure-document-loading" role="status" aria-live="polite">
              <span className="spinner-border" aria-hidden="true" />
              <strong>{text.loadingDocumentPreview}</strong>
            </div>
          )}
          {total > 1 && (
            <button
              type="button"
              className="secure-document-gallery-nav is-previous"
              aria-label={text.previous}
              disabled={loading}
              onClick={onPrevious}
            >
              <i className="fa-solid fa-chevron-left" aria-hidden="true" />
            </button>
          )}
          <img
            src={url}
            alt={title}
            draggable="false"
            className={dragging ? "is-dragging" : "is-draggable"}
            onLoad={() => setImageReady(true)}
            onError={() => setImageReady(true)}
            onPointerDown={startPan}
            onPointerMove={movePan}
            onPointerUp={stopPan}
            onPointerCancel={stopPan}
            onDragStart={(event) => event.preventDefault()}
            onContextMenu={(event) => event.preventDefault()}
            style={{
              opacity: imageReady ? 1 : 0,
              transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${zoom}) rotate(${rotation}deg)`
            }}
          />
          {total > 1 && (
            <button
              type="button"
              className="secure-document-gallery-nav is-next"
              aria-label={text.next}
              disabled={loading}
              onClick={onNext}
            >
              <i className="fa-solid fa-chevron-right" aria-hidden="true" />
            </button>
          )}
        </div>
        {total > 1 && (
          <footer className="secure-document-gallery-footer">
            <span>{index + 1} / {total}</span>
            <div aria-label={text.viewerControls}>
              {Array.from({ length: total }, (_, itemIndex) => (
                <i
                  key={itemIndex}
                  className={itemIndex === index ? "is-active" : ""}
                  aria-hidden="true"
                />
              ))}
            </div>
          </footer>
        )}
      </section>
    </div>
  );
};

export default SecureDocumentViewer;
